import { useState, lazy, Suspense } from "react";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { ArrowRight, ChevronDown, TrendingUp, ShieldCheck, Truck, Star } from "lucide-react";

import { HeroSection } from "@/components/home/HeroSection";
import { PromoPromoSection } from "@/components/home/PromoPromoSection";
import { CategoriesSection } from "@/components/home/CategoriesSection";
import { ProductCard } from "@/components/home/ProductCard";
import { SwipeProductCards } from "@/components/home/SwipeProductCards";
import { GiftPromoStrip } from "@/components/home/GiftPromoStrip";

// Lazy load below-the-fold components
const SellPhoneSection = lazy(() => import("@/components/home/SellPhoneSection").then(m => ({ default: m.SellPhoneSection })));
const TrustSection = lazy(() => import("@/components/home/TrustSection").then(m => ({ default: m.TrustSection })));
const NewsletterSection = lazy(() => import("@/components/home/NewsletterSection").then(m => ({ default: m.NewsletterSection })));
const PartnerStoresSection = lazy(() => import("@/components/home/PartnerStoresSection").then(m => ({ default: m.PartnerStoresSection })));
const BecomePartnerSection = lazy(() => import("@/components/home/SaaSHeroSections").then(m => ({ default: m.BecomePartnerSection })));
const PassportServiceSection = lazy(() => import("@/components/home/SaaSHeroSections").then(m => ({ default: m.PassportServiceSection })));
const Testimonials = lazy(() => import("@/components/Testimonials").then(m => ({ default: m.Testimonials })));

import { useIsMobile } from "@/hooks/use-mobile";
import { usePlatformMode } from "@/hooks/usePlatformMode";
import { useFeatureToggles } from "@/hooks/useFeatureToggle";
import { useDemo } from "@/contexts/DemoContext";

const Index = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { enabled: platformMode } = usePlatformMode();
  const { toggles } = useFeatureToggles();

  const { data: homeData, isLoading: loading } = useQuery({
    queryKey: ["homepage-data"],
    queryFn: async () => {
      const [
        { data: prods, error: prodsErr }, 
        { data: cats, error: catsErr }, 
        { data: secs, error: secsErr }
      ] = await Promise.all([
        (supabase as any).from("products").select("*").eq("status", "active").gt("stock_quantity", 0).order("created_at", { ascending: false }),
        supabase.from("categories").select("*"),
        supabase.from("homepage_sections").select("*").eq("is_enabled", true).order("display_order"),
      ]);

      if (prodsErr) console.error("Error fetching products:", prodsErr);
      if (catsErr) console.error("Error fetching categories:", catsErr);
      if (secsErr) console.error("Error fetching sections:", secsErr);

      const allProducts = (prods || []).filter((p: any) => 
        (!p.status || p.status === "active") && 
        (p.stock_quantity > 0)
      );
      
      return {
        featuredProducts: allProducts.filter((p: any) => p && p.featured).slice(0, 6),
        newArrivals: allProducts.filter((p: any) => p && p.is_new_arrival).slice(0, 8),
        bestDeals: allProducts
          .filter((p: any) => p && p.original_price && p.original_price > p.price)
          .sort((a: any, b: any) => (1 - b.price / b.original_price) - (1 - a.price / a.original_price))
          .slice(0, 6),
        allProducts,
        categories: cats || [],
        sections: secs || [],
      };
    },
    staleTime: 1000 * 60 * 2,
  });

  const featuredProducts = homeData?.featuredProducts || [];
  const newArrivals = homeData?.newArrivals || [];
  const bestDeals = homeData?.bestDeals || [];
  const allProducts = homeData?.allProducts || [];
  const categories = homeData?.categories || [];
  const sections = homeData?.sections || [];

  const isSectionEnabled = (key: string) => sections.find((s) => s.section_key === key)?.is_enabled !== false;

  const faqs = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
    { q: t("faq.q5"), a: t("faq.a5") },
  ];

  const LazyFallback = () => (
    <div className="py-16 px-4">
      <div className="max-w-7xl mx-auto h-48 w-full animate-pulse bg-muted/20 rounded-3xl" />
    </div>
  );

  return (
    <Layout>
      <SEO title="Chawni Phone | Smartphones, Trade-In & Repair in Tangier" description="Buy, sell, trade-in and repair smartphones in Tangier with Chawni Phone — verified devices, fair prices, and a trusted Moroccan ecosystem." path="/" />
      <div className="home-mobile-safe w-full max-w-full overflow-x-clip">
      <HeroSection />

      {(() => {
        const giftSection = sections.find((s) => s.section_key === "gift_promo_strip");
        if (!giftSection || !giftSection.is_enabled) return null;
        const content = (giftSection.content as any) || {};
        const giftProduct = allProducts.find((p: any) => p.id === content.gift_product_id);
        const triggerProduct = allProducts.find((p: any) => p.id === content.trigger_product_id);
        return (
          <GiftPromoStrip
            title={giftSection.title}
            subtitle={giftSection.subtitle}
            giftProduct={giftProduct ? { id: giftProduct.id, name: giftProduct.name, image_url: giftProduct.image_url, slug: giftProduct.slug } : null}
            triggerProduct={triggerProduct ? { id: triggerProduct.id, name: triggerProduct.name, slug: triggerProduct.slug } : null}
            ctaText={content.cta_text || "Shop the offer"}
            ctaLink={content.cta_link || "/shop"}
          />
        );
      })()}

      {sections
        .filter(s => s.section_key?.startsWith("promo_") && s.is_enabled)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(promoSection => {
          const promoProductIds = (promoSection.content as any)?.product_ids || [];
          const promoProducts = allProducts.filter(p => promoProductIds.includes(p.id));
          if (promoProducts.length === 0) return null;
          
          return (
            <PromoPromoSection 
              key={promoSection.id}
              products={promoProducts} 
              config={{
                ...(promoSection.content as any),
                title: promoSection.title,
                subtitle: promoSection.subtitle
              }} 
            />
          );
        })}

      <CategoriesSection categories={categories} isLoading={loading} />


      {newArrivals.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/20">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal>
              <div className="flex min-w-0 flex-col gap-4 mb-8 sm:mb-12 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-primary tracking-[0.2em] uppercase">{t("sections.justIn")}</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mt-2 text-foreground">{t("sections.newArrivals")}</h2>
                </div>
                <Button variant="outline" className="rounded-xl h-11 sm:h-10 px-4 sm:px-5 text-sm font-semibold self-start sm:self-auto" asChild>
                  <Link to="/shop">{t("sections.viewAll")} <ArrowRight className="w-4 h-4 ms-2" /></Link>
                </Button>
              </div>
            </ScrollReveal>
            <StaggerContainer className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {newArrivals.map((product) => (
                <StaggerItem key={product.id}>
                  <ProductCard product={product} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {bestDeals.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal>
              <div className="flex min-w-0 flex-col gap-4 mb-8 sm:mb-12 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-destructive tracking-[0.2em] uppercase">{t("sections.saveBig")}</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mt-2 text-foreground">{t("sections.bestDeals")}</h2>
                </div>
                <Button variant="outline" className="rounded-xl h-11 sm:h-10 px-4 sm:px-5 text-sm font-semibold self-start sm:self-auto" asChild>
                  <Link to="/shop">{t("sections.viewAll")} <ArrowRight className="w-4 h-4 ms-2" /></Link>
                </Button>
              </div>
            </ScrollReveal>
            <StaggerContainer className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {bestDeals.map((product) => (
                <StaggerItem key={product.id}>
                  <ProductCard product={product} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      {featuredProducts.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/20">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal>
              <div className="flex min-w-0 flex-col gap-4 mb-8 sm:mb-12 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <span className="text-xs font-semibold text-primary tracking-[0.2em] uppercase">{t("sections.curated")}</span>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mt-2 text-foreground">{t("sections.featuredProducts")}</h2>
                </div>
                <Button variant="outline" className="rounded-xl h-11 sm:h-10 px-4 sm:px-5 text-sm font-semibold self-start sm:self-auto" asChild>
                  <Link to="/shop">{t("sections.viewAll")} <ArrowRight className="w-4 h-4 ms-2" /></Link>
                </Button>
              </div>
            </ScrollReveal>
            <StaggerContainer className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {featuredProducts.map((product) => (
                <StaggerItem key={product.id}>
                  <ProductCard product={product} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      )}

      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-primary/[0.03]">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: ShieldCheck, label: t("stats.verifiedDevices"), value: "100%" },
                { icon: Truck, label: t("stats.fastDelivery"), value: "24-48h" },
                { icon: Star, label: t("stats.customerRating"), value: "4.9/5" },
                { icon: TrendingUp, label: t("stats.priceMatch"), value: t("stats.guaranteed") },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 sm:p-6 rounded-2xl bg-card border border-border">
                  <stat.icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary mx-auto mb-2" />
                  <p className="text-xl sm:text-2xl font-display font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Suspense fallback={<LazyFallback />}>
        {platformMode && <PartnerStoresSection />}
        {platformMode && <BecomePartnerSection />}
        {platformMode && <PassportServiceSection />}
        {toggles.sell_phone && <SellPhoneSection />}
      </Suspense>

      <Suspense fallback={<LazyFallback />}>
        <TrustSection />
        {toggles.testimonials && <Testimonials />}
        {toggles.newsletter && <NewsletterSection />}
      </Suspense>

      {isSectionEnabled("faq") && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/20">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <span className="text-xs font-semibold text-primary tracking-[0.2em] uppercase">{t("sections.faqHelp")}</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mt-2 sm:mt-3 text-foreground">{t("sections.faq")}</h2>
              </div>
            </ScrollReveal>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <FAQItem key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>
        </section>
      )}
      </div>
    </Layout>
  );
};

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/20 transition-colors">
      <button className="w-full p-5 sm:p-6 flex items-center justify-between text-left" onClick={() => setOpen(!open)}>
        <span className="font-semibold text-sm sm:text-base text-foreground">{question}</span>
        <m.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-4" />
        </m.div>
      </button>
      <m.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground text-sm leading-relaxed">{answer}</div>
      </m.div>
    </div>
  );
};

export default Index;
